import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Lightweight searchable dropdown. Type-to-filter, click to pick.
 * No dependency on Popover/Command — just an Input + a positioned
 * dropdown div. Enough for internal ops tools.
 */
export interface SearchableSelectOption {
  id: string;
  label: string;
  sub?: string | null;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  emptyMessage = 'No match',
  disabled = false,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  options: SearchableSelectOption[];
  placeholder: string;
  emptyMessage?: string;
  disabled?: boolean;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onClickAway = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onClickAway);
    return () => window.removeEventListener('mousedown', onClickAway);
  }, []);

  const selected = value ? options.find((o) => o.id === value) : null;

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options.slice(0, 40);
    return options.filter((o) =>
      o.label.toLowerCase().includes(term)
      || (o.sub && o.sub.toLowerCase().includes(term))
      || o.id.toLowerCase().includes(term),
    ).slice(0, 40);
  }, [q, options]);

  return (
    <div ref={ref} className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        value={open ? q : selected?.label ?? ''}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); setQ(''); }}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      />
      {open && (
        <div className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md">
          {filtered.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">{emptyMessage}</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); setOpen(false); setQ(''); inputRef.current?.blur(); }}
                className={cn(
                  'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                  value === o.id && 'bg-accent/50',
                )}
              >
                <span className="font-medium">{o.label}</span>
                {o.sub && <span className="text-xs text-muted-foreground">{o.sub}</span>}
              </button>
            ))
          )}
        </div>
      )}
      {selected && !open && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(null); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
          aria-label="Clear"
        >
          ×
        </button>
      )}
    </div>
  );
}
